import React, {createRef, useState, useContext, useEffect} from 'react';
import { DragDropContext, Droppable, Draggable } from '@react-forked/dnd';
import {useAuth0} from "@auth0/auth0-react";
import MaterialTable from "@material-table/core";
import { defaultComponents, MaterialTableBody } from '../../../controls/Material-Table-Components';
import __ from "lodash";

import SystemContext from "../../../SystemContext";

import AirportLookup from "../../Airports/components/AirportsLookup";
import {ColumnWidth, ValidatorColumn} from "../../../blocks/ColumnHelper";
import CostingCommissionsValidator from "../validators/CostingCommissionsValidator";
import {MaskedNumber, MaskedText, ComboSelect, ChromeTabs } from '../../../controls';
import { getApiSitePrefix } from "../../../functions/SiteDefaults";
import { useQueryClient } from 'react-query';
import CompanyUuidLookup from "../../Companies/components/CompanyUuidLookup";
import { useTheme } from '@material-ui/core';

const apiSitePrefix = getApiSitePrefix();

const formatChars={
  "U": "[A-Za-z]",
  "A": "[A-Z0-9]",
  "a": "[A-Za-z0-9]",
  "9": "[0-9]",
  "D": "[0-9.]",
  "n": "-",
  "x": "."
}

const SectionHeading = (props) => {
  const { children, ...other } = props;
  return (
    <div style={{marginTop: 10, marginBottom: 2, textAlign: "left", fontSize: "14px", fontWeight: "bold", ...other}}>
      {children}
    </div>
  );
}

const CostingCommissionEditor = (props) => {
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const tableRef = createRef({state: {lastEditingRow: false}});
  const {isAuthenticated} = useAuth0();
  const queryClient = useQueryClient();
  const eventBus = SystemData.eventBus;
  const {record, readOnly} = props;
  const oldProps = __.cloneDeep(props);

  useEffect(()=> {
    const addCommission = async () => {
      if (tableRef  && tableRef.current) {
        tableRef.current.dataManager.changeRowEditing();
        tableRef.current.setState({
          ...tableRef.current.dataManager.getRenderState(),
          showAddRow: !tableRef.current.state.showAddRow,
        });
      }
  }
    if (eventBus) {
      eventBus.bind('CostingCommissionEditor.addCommission', addCommission);    
      return () => {
        eventBus.unbind("CostingCommissionEditor.addCommission", addCommission);
      }
    }

  }, [eventBus, tableRef]);

  const {classes, defaultValues, costingErrors} = props;

  const [state] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });

  const commissions = record.commissions || [];

  const getUplift = (originAirportCode) => {
    const {legs} = record;
    const sector = __.find(legs, {originAirportCode});
    if (sector) {
      return sector.payloadUp;
    }
  }

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { padding: "4px 2px" },
      headerStyle: { padding: "2px 2px" },
      editable: state.canEdit?"always":"never",
      emptyValue: "",
      initialEditValue: "",
      sorting: false
    };
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    const validatorColumn = ValidatorColumn(CostingCommissionsValidator, false);

    return [
      {...validatorColumn, resizable: false},
      {
        ...baseColumn,
        ...ColumnWidth(60,[5,2],undefined,undefined,baseColumn.headerStyle,undefined,false),
        title: "Origin",
        field: "originAirportCode",
        initialEditValue: "",
        editComponent: (props) => {
          return (
            <AirportLookup
              value={props.value}
              returnType="object"
              onChange={(returnObject)=>{
                if (returnObject.found) {
                  const originAirportCode = returnObject.data.airportCode;
                  const quantity = getUplift(originAirportCode);
                  props.onRowDataChange({...props.rowData, originAirportCode, quantity});
                } else {
                  props.onChange(returnObject.value);
                }
              }}
            />
          )
        },
      },
      {
        ...baseColumn,
        ...ColumnWidth(200),
        title: "Company",
        field: "companyName",
        initialEditValue: "",
        render: rowData => (
          <div style={{textOverflow: 'ellipsis', width: 190, whiteSpace: 'nowrap', overflow: 'hidden'}}>{rowData.companyName}</div>
        ),
        editComponent: (props) => (
          <CompanyUuidLookup
            companyType={"CUSTOMER"}
            includeFreesale={true}
            value={props.value}
            returnType={"object"}
            onChange={(returnObject) => {
              if (returnObject.found) {
                const customer = returnObject.data
                const data = {
                  ...props.rowData,
                  companyName: customer.companyName,
                  companyUuid: customer.companyUuid,
                  companyCode: customer.companyCode,
                  branchCode: customer.branchCode
                }
                props.onRowDataChange(data);
              } else {
                props.onChange(returnObject.value);
              }
            }}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(165,2,undefined,undefined,baseColumn.headerStyle,undefined,false),
        title: "Calculation Method",
        field: "calculationMethod",
        initialEditValue: 'COST',
        render: (rowData) => (
          <ComboSelect
            id="legType"
            group="COSTING COMMISSION"
            subGroup={"CALCULATION METHODS"}
            fullWidth
            value={rowData.calculationMethod}
            displayOnly={true}
          />
        ),
        editComponent: (props) => {
          return (
          <ComboSelect
            id="calculationMethod"
            group="COSTING COMMISSION"
            subGroup={"CALCULATION METHODS"}
            fullWidth
            value={props.value}
            onChange={(calculationMethod) => {
              if (calculationMethod) {
                const quantity = getUplift(props.rowData.originAirportCode);
                let totalAmount = 0;
                let unitAmount = props.rowData?.unitAmount || 0;
                if (calculationMethod === "PER_KG") {
                  totalAmount = quantity * (unitAmount || 0);
                }
                if (calculationMethod === "COST") {
                  totalAmount = unitAmount;
                }
                props.onRowDataChange({...props.rowData, calculationMethod, quantity, totalAmount});
              }
            }}
          />
        )},
      },      
      {
        ...baseColumn,
        ...ColumnWidth(95,2,undefined,undefined,undefined,undefined,false),
        title: "Unit Amount",
        field: "unitAmount",
        type: "numeric",
        initialEditValue: 0,
        render: (rowData) => (
          <MaskedNumber 
            displayType="text"
            defaultValue={rowData.unitAmount||0}
            value={rowData.unitAmount||0}
            decimalScale={rowData.calculationMethod==="PER_KG"?4:2}
            fixedDecimalScale={true}
            prefix={rowData.calculationMethod!=="PERCENT_OF_REVENUE"?"$":""}
            suffix={rowData.calculationMethod==="PERCENT_OF_REVENUE"?"%":""}
            inputProps={{ style:{ fontSize: "0.875rem", textAlign: "right" }}}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        editComponent: (props) => (
          <MaskedNumber 
            displayType="input"
            defaultValue={props.value||0}
            value={props.value}
            decimalScale={props.rowData.calculationMethod==="PER_KG"?4:2}
            fixedDecimalScale={true}
            prefix={props.rowData.calculationMethod!=="PERCENT_OF_REVENUE"?"$":""}
            suffix={props.rowData.calculationMethod==="PERCENT_OF_REVENUE"?"%":""}
            inputProps={{ style:{ fontSize: "0.875rem", textAlign: "right" }}}
            onChange={(value) => {
              if (value && value > 0) {
                let totalAmount = 0;
                let unitAmount = value;
                if (props.rowData?.calculationMethod === "PER_KG") {
                  totalAmount = (props.rowData?.quantity||0) * unitAmount;
                }
                if (props.rowData?.calculationMethod === "COST") {
                  totalAmount = unitAmount;
                }
                const data = {...props.rowData, totalAmount, unitAmount} 
                props.onRowDataChange(data);
              } else {
                props.onChange(value);
              }
            }}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(95,2,undefined,undefined,undefined,undefined,false),
        title: "Uplift",
        field: "quantity",
        type: "numeric",
        initialEditValue: 0,
        editable: (col, value, rowData) => {return rowData?.calculationMethod === 'PER_KG'},
        render: (rowData, rowType, updatedData) => (
          (updatedData?.calculationMethod || rowData.calculationMethod) === 'PER_KG'?
            <MaskedNumber 
              displayType="text"
              defaultValue={rowData.quantity||0}
              value={rowData.quantity||0}
              decimalScale={0}
              fixedDecimalScale={true}
              suffix=" kg"
              inputProps={{ style:{ fontSize: "0.875rem", textAlign: "right" }}}
              style={{ fontSize: "0.875rem", textAlign: "right" }}
            />
          : 
            <div style={{marginLeft: 4, fontSize: "0.875rem", width: "100%", textAlign: "center", backgroundColor: "#eeeeee"}}>&nbsp;</div>
          ),
        editComponent: (props) => (
          <MaskedNumber 
            displayType="input"
            defaultValue={props.value||0}
            value={props.value}
            decimalScale={0}
            fixedDecimalScale={true}
            suffix=" kg"
            inputProps={{ style:{ fontSize: "0.875rem", textAlign: "right" }}}
            onChange={(value, x) => {
              if (value && value > 0) {
                let totalAmount = 0;
                let unitAmount = props.rowData.unitAmount;
                let quantity = value;
                if (props.rowData?.calculationMethod === "PER_KG") {
                  totalAmount = quantity * (unitAmount || 0);
                }
                if (props.rowData?.calculationMethod === "COST") {
                  totalAmount = unitAmount;
                }
                const data = {...props.rowData, quantity, totalAmount, unitAmount} 
                props.onRowDataChange(data);
              } else {
                props.onChange(value);
              }
            }}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(95,2,undefined,undefined,undefined,undefined,false),
        title: "Total Amount",
        field: "totalAmount",
        type: "numeric",
        editable: false,
        initialEditValue: 0,
        render: (rowData, rowType, updatedData) => {
          return ((updatedData?.calculationMethod || rowData.calculationMethod) !== 'PERCENT_OF_REVENUE'?
            <MaskedNumber 
              displayType="text"
              defaultValue={rowType === "group"?updatedData?.totalAmount||0:updatedData?.totalAmount||0}
              value={rowType === "group"?updatedData?.totalAmount||0:updatedData?.totalAmount||0}
              decimalScale={2}
              fixedDecimalScale={true}
              prefix={"$"}
              inputProps={{ style:{ fontSize: "0.875rem", textAlign: "right" }}}
              style={{ fontSize: "0.875rem", textAlign: "right" }}
            />
          : 
            <div style={{marginLeft: 4, fontSize: "0.875rem", width: "100%", textAlign: "center", backgroundColor: "#eeeeee"}}>&nbsp;</div>
          )
        }
      },
      {
        ...baseColumn,
        ...ColumnWidth(430, [17,2]),
        title: "Notes",
        field: "notes",
        initialEditValue: "",
        render: rowData => (
          <div style={{textOverflow: 'ellipsis', width: 510, whiteSpace: 'nowrap', overflow: 'hidden'}}>{rowData.notes}</div>
        ),
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(200)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
    ];
  };

  const bodyRef = createRef();
  
  return (isAuthenticated &&
        <div className={classes.costingCommissionEditor}>
          <SectionHeading>Commissions</SectionHeading>
          <MaterialTable
            tableRef={tableRef}
            data={commissions}
            style={{
              backgroundColor: "#fdfdfd",
              boxShadow: "none",
              border: "2px solid #aaaaaa",
              borderRadius: 0,
            }}
            options={{
              actionsColumnIndex: -1,
              search: false,
              cellStyle: {
                padding: "8px 2px",
              },
              headerStyle: {
                backgroundColor: theme.palette.childTableHeader.backgroundColor,
                color: theme.palette.childTableHeader.textColor,
                padding: "2px",
              },
              toolbarButtonAlignment: "right",
              detailPanelType: "single",
              showTitle: false,
              toolbar: false,
              padding: 'dense',
              paging: false,
              filtering: false,
              draggable: false,
            }}
            components={{
              ...defaultComponents,
              Body: (props) => (
                <MaterialTableBody
                  {...props}
                  ref={bodyRef} 
                  rowHeight={40}
                  overscanRowCount={10}
                  addVirtualScrolling={true}
                  extraEditHeight={46}
                  headerHeight={46}
                />
              ),
            }}
            title="Commission Consignments"
            columns={getColumns({...props, canEdit: state.canEdit})}
            editable={{
              isEditable: () => {
                return state.canEdit && !readOnly;
              },
              isDeletable: () => {
                return state.canEdit && !readOnly;
              },
              onRowAdd: state.canEdit && !readOnly
                ? (newData) => new Promise((resolve, reject) => {
                  if (Array.isArray(commissions)) {
                    commissions.push(newData);
                    props.onChange({commissions, oldCommissions: null, costingErrors, resolve, reject});
                  } else {
                    reject();
                  }
                })
                : undefined,
              onRowUpdate: state.canEdit && !readOnly
              ?(newData, oldData) =>
                new Promise((resolve, reject) => {
                  const commissionIdx = __.findIndex(commissions, (consignment) => {
                    const tripData = __.omit(consignment, ["tableData"]);
                    const oldRecord = __.omit(oldData, ["tableData"]);
                    return __.isEqual(tripData, oldRecord);
                  }); 
                  if (commissionIdx >= 0) {
                    commissions[commissionIdx] = newData;
                    props.onChange({commissions, oldCommissions: oldData?.commissions, resolve, reject});
                  } else {
                    reject();
                  }
                })
                :null,
              onRowDelete: state.canEdit && !readOnly
              ?(oldData) =>
                new Promise((resolve, reject) => {
                  const commissionIdx = __.findIndex(commissions, (consignment) => {
                    const tripData = __.omit(consignment, ["tableData"]);
                    const oldRecord = __.omit(oldData, ["tableData"]);
                    return __.isEqual(tripData, oldRecord);
                  }); 
                  if (commissionIdx >= 0) {
                    commissions.splice(commissionIdx, 1);
                    props.onChange({commissions, oldCommissions: oldData?.commissions, resolve, reject});
                  } else {
                    reject();
                  }
                })
                : null,
            }}
        />
      </div>
  );
}

export default CostingCommissionEditor;


